// Anke, 2026-07-19 (Fehlerbericht) — "wenn ich in eine Textbox NUR den Linktext
// eingebe, wird der Pfeil nicht gezeichnet; erst wenn ich noch Text dahinter
// schreibe."
//
// Ursache: TipTaps `autolink` setzt die Link-Markierung an einer WORTGRENZE —
// beim Leerzeichen, beim Absatz, beim nächsten Zeichen. Endet das Dokument
// exakt auf der Adresse, kommt diese Grenze nie, und ohne Markierung gibt es
// auch keinen Pfeil.
//
// Dieser Durchlauf holt das beim Verlassen der Textbox nach: Er markiert jede
// noch unmarkierte Adresse im fertigen Dokument. Damit ist der gespeicherte
// Zustand vollständig, egal wo die Adresse endet.
//
// Erkannt wird mit `linkifyjs` — GENAU der Bibliothek, die TipTaps `autolink`
// selbst benutzt. Ein eigener regulärer Ausdruck hätte sonst eine zweite
// Wahrheit geschaffen: Adressen, die beim Tippen verlinkt werden, aber hier
// nicht (oder umgekehrt).

import { find } from 'linkifyjs';
import type { TextDocument, TextDocumentNode, TextDocumentMark } from './text-document.type.js';

/** Whether a node already carries a link mark — those are left untouched. */
function hasLink(node: TextDocumentNode): boolean {
  return (node.marks ?? []).some((m) => m.type === 'link');
}

/** Split one unmarked text node into plain / linked pieces. */
function linkifyTextNode(node: TextDocumentNode): TextDocumentNode[] {
  const text = node.text ?? '';
  if (text === '') return [node];

  const matches = find(text).filter((m) => m.isLink);
  if (matches.length === 0) return [node];

  const out: TextDocumentNode[] = [];
  let cursor = 0;
  const marks = node.marks ?? [];
  for (const m of matches) {
    if (m.start > cursor) {
      out.push({ ...node, text: text.slice(cursor, m.start) });
    }
    const linkMark: TextDocumentMark = { type: 'link', attrs: { href: m.href } };
    out.push({ ...node, text: text.slice(m.start, m.end), marks: [...marks, linkMark] });
    cursor = m.end;
  }
  if (cursor < text.length) {
    out.push({ ...node, text: text.slice(cursor) });
  }
  return out;
}

function walk(node: TextDocumentNode): TextDocumentNode {
  if (node.content) {
    return { ...node, content: node.content.flatMap((c) => walkMany(c)) };
  }
  return node;
}

function walkMany(node: TextDocumentNode): TextDocumentNode[] {
  if (node.type === 'text') {
    // An address the user marked as a link by hand — or that autolink already
    // caught — keeps whatever href it has. Re-detecting could overwrite a
    // deliberately different target with the visible text.
    return hasLink(node) ? [node] : linkifyTextNode(node);
  }
  return [walk(node)];
}

/**
 * Mark every not-yet-linked URL in `doc`. Pure — returns a new document and
 * never mutates the input. Returns the input unchanged when nothing matched, so
 * a commit without links stays a cheap no-op.
 */
export function autolinkDocument(doc: TextDocument): TextDocument {
  return walk(doc as TextDocumentNode) as TextDocument;
}
